import React, { useState, useMemo } from 'react';
import psychicPowersData from '../../../data/psychic-powers.json';

interface PsychicPowersTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedRulebookIds: string[];
}

export const PsychicPowersTab: React.FC<PsychicPowersTabProps> = ({
  searchTerm,
  setSearchTerm,
  selectedRulebookIds
}) => {
  interface PsychicPower {
    id: string;
    name: string;
    school: string;
    tree: string;
    focusTime: string;
    sustain: boolean;
    range: string;
    prerequisite: string | null;
    xpValue: number;
    focusCharacteristic: string | null;
    description: string;
    overbleed: string | null;
    bookPage: string | null;
    rulebook: string;
  }

  const powers = psychicPowersData.powers as PsychicPower[];
  const [selectedSchool, setSelectedSchool] = useState<string>('all');
  const [selectedTree, setSelectedTree] = useState<string>('all');

  const visiblePowers = useMemo(
    () => powers.filter((power) => selectedRulebookIds.includes(power.rulebook)),
    [powers, selectedRulebookIds]
  );

  const schools = useMemo(() => {
    const schoolSet = new Set(visiblePowers.map((p) => p.school));
    return Array.from(schoolSet).sort();
  }, [visiblePowers]);

  const trees = useMemo(() => {
    let filtered = visiblePowers;
    if (selectedSchool !== 'all') {
      filtered = filtered.filter((p) => p.school === selectedSchool);
    }
    const treeSet = new Set(filtered.map((p) => p.tree));
    return Array.from(treeSet).sort();
  }, [visiblePowers, selectedSchool]);

  const filteredPowers = useMemo(() => {
    let filtered = visiblePowers;

    if (selectedSchool !== 'all') {
      filtered = filtered.filter((p) => p.school === selectedSchool);
    }

    if (selectedTree !== 'all') {
      filtered = filtered.filter((p) => p.tree === selectedTree);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (power) =>
          power.name.toLowerCase().includes(term) ||
          power.description.toLowerCase().includes(term) ||
          (power.prerequisite && power.prerequisite.toLowerCase().includes(term)) ||
          power.tree.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [searchTerm, selectedSchool, selectedTree, visiblePowers]);

  const getSchoolColor = (school: string): string => {
    const colors: Record<string, string> = {
      TELEPATHY: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
      DIVINATION: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
      TELEKINESIS: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200',
      BIOMANCY: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
      PYROMANCY: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
      'SOUL WARD': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
      THEOSOPHAMY: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200',
      VOIDFROST: 'bg-slate-100 dark:bg-slate-900/30 text-slate-800 dark:text-slate-200',
      'SORCERER POWERS': 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200'
    };
    return colors[school] || 'bg-surface-hover text-ink';
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Search Powers</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, description, or prerequisite..."
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">School/Discipline</label>
            <select
              value={selectedSchool}
              onChange={(e) => {
                setSelectedSchool(e.target.value);
                setSelectedTree('all');
              }}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Schools</option>
              {schools.map((school) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Tree</label>
            <select
              value={selectedTree}
              onChange={(e) => setSelectedTree(e.target.value)}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Trees</option>
              {trees.map((tree) => (
                <option key={tree} value={tree}>
                  {tree}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-2 text-sm text-ink-dim">
          Showing {filteredPowers.length} of {visiblePowers.length} psychic powers
        </div>
      </div>

      {/* Powers List */}
      <div className="space-y-3">
        {filteredPowers.map((power) => (
          <div
            key={power.id}
            className="group bg-surface border border-line rounded-lg p-4 hover:shadow-lg transition-shadow"
            title={`${power.description}\n\nFocus Time: ${power.focusTime}\nRange: ${power.range}\nSustain: ${power.sustain ? 'Yes' : 'No'}`}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-ink">{power.name}</h3>
              <div className="flex gap-2 flex-wrap justify-end">
                <span className={`px-2 py-1 text-xs rounded ${getSchoolColor(power.school)}`}>
                  {power.school}
                </span>
                <span className="px-2 py-1 text-xs rounded bg-surface-hover text-ink">
                  {power.tree}
                </span>
              </div>
            </div>

            {/* Power Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm">
              <div>
                <span className="font-medium text-ink-dim">Focus Time:</span>
                <span className="ml-1 text-ink">{power.focusTime}</span>
              </div>
              <div>
                <span className="font-medium text-ink-dim">Range:</span>
                <span className="ml-1 text-ink">{power.range}</span>
              </div>
              <div>
                <span className="font-medium text-ink-dim">Sustain:</span>
                <span className="ml-1 text-ink">{power.sustain ? 'Yes' : 'No'}</span>
              </div>
              {power.xpValue > 0 && (
                <div>
                  <span className="font-medium text-ink-dim">XP Cost:</span>
                  <span className="ml-1 text-ink">{power.xpValue}</span>
                </div>
              )}
            </div>

            {/* Focus Characteristic */}
            {power.focusCharacteristic && power.focusCharacteristic !== '-' && (
              <div className="mb-2 text-sm">
                <span className="font-medium text-ink-dim">Focus Characteristic:</span>
                <span className="ml-1 text-ink">{power.focusCharacteristic}</span>
              </div>
            )}

            {/* Prerequisites */}
            {power.prerequisite && power.prerequisite !== '-' && (
              <div className="mb-2">
                <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                  Prerequisites:{' '}
                </span>
                <span className="text-xs text-ink">{power.prerequisite}</span>
              </div>
            )}

            {/* Description */}
            <p className="text-ink text-sm mb-2">{power.description}</p>

            {/* Overbleed (Mechanical Effect) */}
            {power.overbleed && (
              <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border-l-4 border-blue-500">
                <span className="text-xs font-semibold text-blue-900 dark:text-blue-100">
                  Overbleed Effect:{' '}
                </span>
                <span className="text-xs text-blue-800 dark:text-blue-200">{power.overbleed}</span>
              </div>
            )}

            {/* Book Reference */}
            {power.bookPage && (
              <div className="mt-2 text-xs text-ink-faint">Reference: {power.bookPage}</div>
            )}
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredPowers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-ink-faint text-lg">No psychic powers found matching your criteria</p>
        </div>
      )}
    </div>
  );
};
